import { useEffect, useId, useRef } from 'react';


import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { TextArea } from 'ui/text-area';

import {
  DECISION_ACTIONS,
  NOTE_MAX_LENGTH,
  NOTE_TEXTAREA_ROWS,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type {
  DecisionActionConfig,
  DecisionActionKey,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { useDecisionPanel } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-decision-panel';

interface DecisionPanelProps {
  clientId: number;
  id: number;
  action: DecisionActionKey;
  onCancel: () => void;
  onDone: () => void;
}

const DecisionPanel = ({
  clientId,
  id,
  action,
  onCancel,
  onDone,
}: DecisionPanelProps) => {
  const titleId = useId();
  const textareaId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const config: DecisionActionConfig = DECISION_ACTIONS[action];
  const { reason, setReason, canConfirm, isPending, handleConfirm } =
    useDecisionPanel({ clientId, id, action, onDone });

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <Stack
      role="group"
      aria-labelledby={titleId}
      sx={{
        width: '100%',
        gap: 1.25,
        borderTopWidth: '1px',
        borderColor: 'tokens.navy.100',
        bgcolor: 'tokens.gray.50',
        px: 2,
        pb: 2,
        pt: 1.5,
      }}
      data-testid={`decision-panel-${action}`}
    >
      <Stack sx={{ gap: '1px' }}>
        <p id={titleId} className="text-base font-semibold text-gray-700">
          {config.title}
        </p>
        <p className="text-sm text-gray-700">{config.description}</p>
      </Stack>
      <TextArea
        ref={textareaRef}
        label={config.field.label}
        id={textareaId}
        required
        helperText={config.field.helper}
        maxLength={NOTE_MAX_LENGTH}
        showCharCount={false}
        rows={NOTE_TEXTAREA_ROWS}
        value={reason}
        onChange={setReason}
        data-testid={`decision-panel-${action}-reason`}
      />
      <Stack direction="row" sx={{ gap: 1.5 }}>
        <Button
          variant={config.confirmVariant}
          size="sm"
          disabled={!canConfirm}
          loading={isPending}
          onClick={handleConfirm}
          data-testid={`decision-panel-${action}-confirm`}
        >
          {config.confirmLabel}
        </Button>
        <Button
          variant="neutral-outlined"
          size="sm"
          onClick={onCancel}
          data-testid={`decision-panel-${action}-cancel`}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default DecisionPanel;
